import { useStore } from "@nanostores/preact"
import { useEffect, useState } from "preact/hooks"
import { loadStripe } from "@stripe/stripe-js/pure"
import { Stripe, PaymentRequest } from "@stripe/stripe-js"
import {
  Elements,
  PaymentRequestButtonElement,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js"
import classNames from "utils/preact/class-names"

import { Currency, IsoCountry, Price, Products } from "../services/support"
import { profile } from "../services/store"
import { useTranslation } from "../components/Translation"

import css from "../style.module.scss"
import cards from "../static/cards.png"
import { SubscriptionButtons } from "../components/SubscriptionButtons"
import { Sidebar } from "../components/Sidebar"

function pay(
  url: string,
  token: string,
  body: string,
): Promise<undefined | Record<string, string>> {
  return fetch(url, {
    method: "post",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": token,
    },
    credentials: "include",
    body,
  })
    .then((res) => {
      if (!res.ok && res.status !== 422 && res.status !== 401) {
        return {
          base: `${res.status}: ${res.statusText}`,
        }
      }

      return res.json()
    })
    .then((res) => {
      if (res.success) return undefined
      if (res.errors) return res.errors
      return res
    })
    .catch((err: Error) => ({
      base: err.message,
    }))
}

function PaymentForm({
  price,
  country,
  currency,
  token,
  features,
  onPay,
}: {
  price: Price
  country: IsoCountry
  currency: Currency
  token: string
  features?: { promo_code_field: boolean }
  onPay: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const user = useStore(profile)
  const t = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null)
  const [promoCode, setPromoCode] = useState("")
  const [promoValidation, setPromoValidation] =
    useState<{
      valid?: boolean
      error?: string
      discount?: {
        percent_off?: number
        amount_off?: number
        currency?: string
        duration?: string
        duration_in_months?: number
        name?: string
      }
    } | null>(null)
  const [isValidatingPromo, setIsValidatingPromo] = useState(false)
  const [showPromoField, setShowPromoField] = useState(false)

  const promoValidationValid = promoValidation?.valid

  useEffect(() => {
    if (stripe) {
      ;(async () => {
        const pr = stripe.paymentRequest({
          country: country.iso.toUpperCase(),
          currency: currency.acronym.toLowerCase(),
          total: {
            label: `Support ${user.subscription}`,
            amount: price.unit_amount,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        })
        const canMakePayment = await pr.canMakePayment()
        if (canMakePayment) {
          setPaymentRequest(pr)
        }
      })()
    }
  }, [stripe, country, currency, price, user.subscription])

  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on("paymentmethod", async (paymentResponse) => {
        setIsLoading(true)
        setSubmitError("")

        const result = await pay(
          "/sign_up/stripe_payment",
          token,
          JSON.stringify({
            stripe_payment: {
              price_id: price.price_id,
              promo_code: promoValidationValid ? promoCode : undefined,
            },
            source: user.source,
          }),
        )

        if (result && result.clientSecret) {
          const { paymentIntent, error: confirmError } = await stripe.confirmCardPayment(
            result.clientSecret,
            { payment_method: paymentResponse.paymentMethod.id },
            { handleActions: false },
          )

          if (confirmError) {
            // eslint-disable-next-line no-console
            console.error(confirmError)

            setIsLoading(false)
            setSubmitError(confirmError.message)
            paymentResponse.complete("fail")
            return
          }

          paymentResponse.complete("success")

          if (paymentIntent.status === "requires_action") {
            const { error } = await stripe.confirmCardPayment(result.clientSecret)
            if (error) {
              // eslint-disable-next-line no-console
              console.error(error)

              setIsLoading(false)
              setSubmitError(error.message)
              return
            }
          }

          profile.setKey("premium", true)
          profile.setKey("reloadRequired", true)
          onPay()
        } else if (result && result.base) setSubmitError(result.base)
        else setSubmitError("Something went wrong")

        setIsLoading(false)
      })
    }

    return () => {
      if (paymentRequest) paymentRequest.off("paymentmethod")
    }
  }, [stripe, paymentRequest, token, price, onPay, user, promoCode, promoValidationValid])

  const clearPromoCode = () => {
    setPromoCode("")
    setPromoValidation(null)
    setShowPromoField(false)
  }

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoValidation({ error: "Please enter a promo code" })
      return
    }

    setIsValidatingPromo(true)
    setPromoValidation(null)

    try {
      const response = await fetch("/sign_up/validate_promo_code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token,
        },
        credentials: "include",
        body: JSON.stringify({ code: promoCode }),
      })

      const data = await response.json()

      if (response.ok && data.valid) {
        setPromoValidation({
          valid: true,
          discount: data.discount,
        })
      } else {
        setPromoValidation({
          error: data.error || "Invalid promo code",
        })
      }
    } catch (error) {
      setPromoValidation({
        error: "Failed to validate promo code",
      })
    } finally {
      setIsValidatingPromo(false)
    }
  }

  const calculateDiscountedPrice = () => {
    if (!promoValidation?.valid || !promoValidation.discount) {
      return price.unit_amount / 100
    }

    const { discount } = promoValidation
    const originalPrice = price.unit_amount / 100

    if (discount.percent_off) {
      return originalPrice * (1 - discount.percent_off / 100)
    }
    if (discount.amount_off) {
      // amount_off is in cents
      return Math.max(0, originalPrice - discount.amount_off / 100)
    }

    return originalPrice
  }

  const handleSubmit = async (event: Event) => {
    event.preventDefault()

    setIsLoading(true)
    setSubmitError("")

    const response = await pay(
      "/sign_up/stripe_payment",
      token,
      JSON.stringify({
        stripe_payment: {
          price_id: price.price_id,
          promo_code: promoValidationValid ? promoCode : undefined,
        },
        source: user.source,
      }),
    )

    if (response && response.clientSecret) {
      const { paymentIntent, error } = await stripe.confirmCardPayment(response.clientSecret, {
        payment_method: {
          card: elements.getElement("card"),
        },
      })

      if (error) {
        // eslint-disable-next-line no-console
        console.error("failed while paying using filled in card details", error)
        setSubmitError(error.message)
      } else if (paymentIntent.status === "succeeded") {
        profile.setKey("premium", true)
        profile.setKey("reloadRequired", true)
        onPay()
      } else {
        setSubmitError(`Something went wrong.\n(paymentIntent.status is ${paymentIntent.status})`)
      }
    } else if (response && response.base) {
      setSubmitError(response.base)
    } else {
      setSubmitError("Something went wrong")
    }

    setIsLoading(false)
  }

  return (
    <form action="/sign_up/stripe_payment" method="post" onSubmit={handleSubmit}>
      {paymentRequest && (
        <div className={css["spacer-bottom"]}>
          <PaymentRequestButtonElement options={{ paymentRequest }} />
        </div>
      )}
      <img
        src={cards}
        alt="Payment providers"
        width={217}
        height={20}
        className={css["spacer-bottom"]}
      />
      <div className={css["card-wrapper"]}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                lineHeight: "1.5",
              },
            },
          }}
        />
      </div>
      <div className={css["spacer-bottom"]} />
      {features?.promo_code_field && (
        <>
          {!showPromoField && !promoValidation?.valid && (
            <button
              type="button"
              onClick={() => setShowPromoField(true)}
              className={css["promo-code-link"]}
            >
              {t("have_promo_code") || "Have a promo code or gift card?"}
            </button>
          )}
          {(showPromoField || promoValidation?.valid) && (
            <>
              <div className={css["promo-code-wrapper"]}>
                <input
                  type="text"
                  placeholder={t("promo_code_placeholder") || "Enter promo code"}
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode((e.target as HTMLInputElement).value)
                    setPromoValidation(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      if (promoCode.trim() && !isValidatingPromo) {
                        validatePromoCode()
                      }
                    }
                  }}
                  className={css["promo-code-input"]}
                  disabled={isValidatingPromo}
                />
                <button
                  type="button"
                  onClick={validatePromoCode}
                  disabled={isValidatingPromo || !promoCode.trim()}
                  className={css["promo-code-button"]}
                >
                  {isValidatingPromo ? t("validating") || "Validating..." : t("apply") || "Apply"}
                </button>
              </div>
              {promoValidation?.valid && promoValidation.discount && (
                <div className={css["promo-success"]}>
                  <button
                    type="button"
                    onClick={clearPromoCode}
                    className={css["promo-clear-button"]}
                    aria-label="Remove promo code"
                  >
                    ×
                  </button>
                  {promoValidation.discount.percent_off
                    ? `${promoValidation.discount.percent_off}% discount applied!`
                    : `${currency.prefix}${
                        promoValidation.discount.amount_off / 100
                      } discount applied!`}
                  {promoValidation.discount.duration === "repeating" &&
                    promoValidation.discount.duration_in_months &&
                    ` (for ${promoValidation.discount.duration_in_months} months)`}
                  {promoValidation.discount.duration === "once" && " (first payment only)"}
                  {promoValidation.discount.duration === "forever" && " (forever)"}
                  <div className={css["discounted-price"]}>
                    New price: {currency.prefix}
                    {calculateDiscountedPrice().toFixed(2)}
                  </div>
                </div>
              )}
              {promoValidation?.error && (
                <div className={css["promo-error"]}>{promoValidation.error}</div>
              )}
            </>
          )}
          <div className={css["spacer-bottom"]} />
        </>
      )}
      {submitError && (
        <p
          className={classNames({
            [css["text-medium"]]: true,
            [css["text-center"]]: true,
            [css["error-highlight"]]: true,
          })}
        >
          {submitError}
        </p>
      )}
      <button
        className={classNames({
          [css.submit]: true,
          [css["is-loading"]]: isLoading,
        })}
        type="submit"
      >
        {t("pay_now")}
      </button>
    </form>
  )
}

export function Payment({
  token,
  products,
  currencies,
  isoCountries,
  publishableKey,
  features,
  onPay,
}: {
  token: string
  products: Products
  currencies: Currency[]
  isoCountries: IsoCountry[]
  publishableKey: string
  features?: { promo_code_field: boolean }
  onPay: () => void
}) {
  const [stripe, setStripe] = useState<Stripe | null>(null)

  useEffect(() => {
    ;(async () => {
      const resolved = await loadStripe(publishableKey)
      setStripe(resolved)
    })()
  }, [publishableKey])

  const t = useTranslation()
  const user = useStore(profile)
  const country = isoCountries.find((c) => c.iso === user.countryIso)
  const currency = currencies.find((c) => c.acronym === country.currency)
  const price = products[user.subscription].prices[currency.acronym]
  // console.log(user)
  // console.log(country)
  // console.log(currency)
  // console.log(price)

  return (
    <div className={css.content}>
      <div className={css["split-container"]}>
        <div className={css["flex-grow"]}>
          <Sidebar />
          <SubscriptionButtons />
        </div>
        <div className={css["horizontal-divider"]} />
        <div className={css["flex-grow"]}>
          <h3 className={css["form-title"]}>{t("payment_information")}</h3>
          <p className={css["account-text"]}>
            {t("account")}
            {": "}
            {user.email || profile.get().email}
          </p>

          <div className={css["payment-info"]}>
            {user.subscription === "monthly" && (
              <p className={css["payment-membership"]}>
                {t("monthly")} {t("membership")}
              </p>
            )}
            {user.subscription === "annually" && (
              <p className={css["payment-membership"]}>
                {t("annual")} {t("membership")}
              </p>
            )}
            <p className={css["payment-amount"]}>
              {currency.prefix}
              {price.unit_amount / 100}
            </p>
          </div>
          {user.subscription === "monthly" && (
            <p className={css["payment-billing"]}>
              {t("billing_info_monthly", {
                price: `${currency.prefix}${price.unit_amount / 100}`,
              })}
            </p>
          )}
          {user.subscription === "annually" && (
            <p className={css["payment-billing"]}>
              {t("billing_info_annually", {
                price: `${currency.prefix}${price.unit_amount / 100}`,
                pricePer: `${currency.prefix}${(price.unit_amount / 1200).toFixed(2)}`,
              })}
            </p>
          )}
          <div className={css["payment-form"]}>
            {stripe ? (
              <Elements stripe={stripe}>
                <PaymentForm
                  token={token}
                  price={price}
                  country={country}
                  currency={currency}
                  features={features}
                  onPay={onPay}
                />
              </Elements>
            ) : (
              <div className={css["text-center"]}>
                <div className={css.loader} />
                <div className={css["text-medium"]}>Loading payment module...</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
